import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { BrandLockup } from "@/features/marketing/components/BrandLockup";
import { resetPassword } from "@/lib/api";

type State = "form" | "done" | "invalid";

/** Landing page for the link in the reset email (`?token=…`). */
export function ResetPasswordPage() {
  const [state, setState] = useState<State>("form");
  const [pending, setPending] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password") ?? "");
    if (password !== String(data.get("confirm") ?? "")) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setPending(true);
    try {
      await resetPassword(token, password);
      setState("done");
    } catch {
      setState("invalid");
    } finally {
      setPending(false);
    }
  };

  const heading =
    state === "done"
      ? "Password changed"
      : state === "invalid"
        ? "Link not valid"
        : "Choose a new password";

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-4 px-6 py-16">
      <div className="flex items-center justify-center">
        <BrandLockup className="h-14 w-14" />
      </div>
      <Card>
        <CardHeader className="justify-center text-center">
          <div>
            <CardTitle className="text-xl">{heading}</CardTitle>
            <CardDescription>
              {state === "done"
                ? "You've been signed out everywhere. Sign in with your new password."
                : state === "invalid" || !token
                  ? "This link has expired or was already used. Ask for a new reset link from the sign-in page."
                  : "At least 12 characters. Any other signed-in device will be signed out."}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {state === "form" && token ? (
            <form className="grid gap-5" aria-busy={pending} onSubmit={submit}>
              <label className="grid gap-2 text-sm" htmlFor="reset-password">
                New password
                <input
                  autoComplete="new-password"
                  className="input"
                  disabled={pending}
                  id="reset-password"
                  minLength={12}
                  name="password"
                  required
                  type="password"
                />
              </label>
              <label className="grid gap-2 text-sm" htmlFor="reset-confirm">
                Repeat it
                <input
                  autoComplete="new-password"
                  className="input"
                  disabled={pending}
                  id="reset-confirm"
                  minLength={12}
                  name="confirm"
                  required
                  type="password"
                />
              </label>
              {mismatch ? (
                <p role="alert" className="text-xs text-[var(--danger)]">
                  The two passwords don't match.
                </p>
              ) : null}
              <Button className="w-full" disabled={pending} type="submit">
                {pending ? "Saving..." : "Change password"}
              </Button>
            </form>
          ) : (
            <Button asChild className="w-full">
              <Link to="/login">Go to sign in</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
